import {
  ANALYTICS_EVENT_NAME_PATTERN,
  ANALYTICS_SOURCES,
  type AnalyticsSource,
} from "@loot-os/api-contract";

import { DAY_MS, HOUR_MS } from "./timeBuckets";

/** The event log page's vocabulary and URL state, shared with the router's `validateSearch`. */

/** Trailing windows the log offers; the widest is the most one read may span. */
export const EVENT_LOG_WINDOWS = ["1h", "24h", "7d"] as const;
export type EventLogWindow = (typeof EVENT_LOG_WINDOWS)[number];

export const EVENT_LOG_WINDOW_LABELS: Record<EventLogWindow, string> = {
  "1h": "Last hour",
  "24h": "Last 24 hours",
  "7d": "Last 7 days",
};

const WINDOW_MS: Record<EventLogWindow, number> = {
  "1h": HOUR_MS,
  "24h": DAY_MS,
  "7d": 7 * DAY_MS,
};

export const DEFAULT_EVENT_LOG_WINDOW: EventLogWindow = "24h";

export function isEventLogWindow(value: unknown): value is EventLogWindow {
  return typeof value === "string" && (EVENT_LOG_WINDOWS as readonly string[]).includes(value);
}

function isAnalyticsSource(value: unknown): value is AnalyticsSource {
  return typeof value === "string" && (ANALYTICS_SOURCES as readonly string[]).includes(value);
}

/** What the page reads from the URL; every filter is optional and absent means "any". */
export interface EventLogSearch {
  /** The trailing window; ignored while `day` is set. */
  w?: EventLogWindow;
  /** One calendar day, `YYYY-MM-DD` in local time, read whole instead of a trailing window. */
  day?: string;
  event?: string;
  source?: AnalyticsSource;
  /** End user id. */
  user?: string;
  /** Anonymous (per-browser) id. */
  anon?: string;
  /** Session (per-tab) id. */
  session?: string;
}

const DAY = /^\d{4}-\d{2}-\d{2}$/;

function text(value: unknown): string | undefined {
  return typeof value === "string" && value ? value : undefined;
}

/** The route's `validateSearch`: anything malformed is dropped rather than refused. */
export function parseEventLogSearch(search: Record<string, unknown>): EventLogSearch {
  const day = text(search.day);
  const event = text(search.event);
  return {
    w: isEventLogWindow(search.w) ? search.w : undefined,
    day: day && DAY.test(day) && localDayStart(day) ? day : undefined,
    event: event && ANALYTICS_EVENT_NAME_PATTERN.test(event) ? event : undefined,
    source: isAnalyticsSource(search.source) ? search.source : undefined,
    user: text(search.user),
    anon: text(search.anon),
    session: text(search.session),
  };
}

/** The local midnight a day key names, or undefined when it names no date. */
export function localDayStart(day: string): Date | undefined {
  const date = new Date(`${day}T00:00:00`);
  return Number.isNaN(date.getTime()) ? undefined : date;
}

/** A calendar day as the URL carries it, `YYYY-MM-DD` in local time. */
export function toDayKey(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/**
 * The half-open [from, to) the page asks for: the chosen day from its local midnight to the
 * next (a 23- or 25-hour day across a clock change), else the window trailing `now`.
 */
export function eventLogRange(
  search: Pick<EventLogSearch, "w" | "day">,
  now: Date,
): { from: Date; to: Date } {
  const start = search.day ? localDayStart(search.day) : undefined;
  if (start) {
    const to = new Date(start);
    to.setDate(to.getDate() + 1);
    return { from: start, to };
  }
  const span = WINDOW_MS[search.w ?? DEFAULT_EVENT_LOG_WINDOW];
  return { from: new Date(now.getTime() - span), to: now };
}

/**
 * An event's stored properties as an object. The column is a JSON object in a string; anything
 * else (a row written by hand, a truncated value) reads as null and the page shows the raw text.
 */
export function parseEventProperties(json: string): Record<string, unknown> | null {
  try {
    const value: unknown = JSON.parse(json);
    return value && typeof value === "object" && !Array.isArray(value)
      ? (value as Record<string, unknown>)
      : null;
  } catch {
    return null;
  }
}

/** One property as the page prints it: a string as it is, anything else as JSON. */
export interface EventPropertyEntry {
  key: string;
  value: string;
  /** Whether the stored value was a string, the kind worth copying or linking. */
  isText: boolean;
}

/** The stored properties as printable entries in stored order, or null when they do not decode. */
export function eventPropertyEntries(json: string): EventPropertyEntry[] | null {
  const properties = parseEventProperties(json);
  if (!properties) return null;
  return Object.entries(properties).map(([key, value]) =>
    typeof value === "string"
      ? { key, value, isText: true }
      : { key, value: JSON.stringify(value), isText: false },
  );
}

/**
 * The entries a table row prints: all of them, less a `route` the Route column beside it already
 * shows (a page view's only property). The expanded row still lists every property.
 */
export function rowPropertyEntries(
  json: string,
  route: string | null,
): EventPropertyEntry[] | null {
  const entries = eventPropertyEntries(json);
  if (!entries) return null;
  return entries.filter((entry) => !(entry.key === "route" && entry.value === route));
}

/** The properties on one line, for a truncated cell's tooltip: `boxId: box_1 · quantity: 2`. */
export function summarizeEventProperties(json: string): string {
  const entries = eventPropertyEntries(json);
  if (!entries) return json;
  return entries.map(({ key, value }) => `${key}: ${value}`).join(" · ");
}

/**
 * How long after it occurred an event was received, signed: `+1.6 s`, `+340 ms`, `−2 min` when
 * the browser's clock runs ahead of ours. Null when the two agree or either is not a date.
 */
export function formatEventDelay(occurredAt: string, receivedAt: string): string | null {
  const ms = new Date(receivedAt).getTime() - new Date(occurredAt).getTime();
  if (!Number.isFinite(ms) || ms === 0) return null;
  const sign = ms < 0 ? "−" : "+";
  const size = Math.abs(ms);
  if (size < 1000) return `${sign}${size} ms`;
  if (size < 60_000) return `${sign}${(size / 1000).toFixed(1)} s`;
  return `${sign}${Math.round(size / 60_000)} min`;
}

/** A row of the log as `analytics.events` answers it. */
export interface EventLogRow {
  id: string;
  tenantId: string;
  environmentId: string;
  endUserId: string | null;
  endUser: { id: string; username: string | null; externalId: string } | null;
  anonymousId: string | null;
  sessionId: string | null;
  eventName: string;
  label: string;
  source: string;
  occurredAt: string;
  receivedAt: string;
  dt: string;
  route: string | null;
  userAgent: string | null;
  /** The dimensions the event was stamped with (`ANALYTICS_DIMENSIONS`); null when it has none. */
  os: string | null;
  device: string | null;
  country: string | null;
  segment: string | null;
  properties: string;
}
